import React, { useState, useEffect } from "react";
import {
  getExaminerCitationsDetails,
  processVerifiedPatents,
  startVijilize,
  addExaminerCitationsToProject,
  getPatentDetails,
} from "services/project";
import Toast from "../Toast";
import allImgPaths from "assets/images";
import SearchBar from "../SearchBar";
import CustomCheckBox from "components/CustomCheckBox";
import { PROJECT_CONSTANTS, VIJILIZE_CONSTANTS } from "utils/constant";
import { InputTextField } from "components/InputField";
import Textarea from "../Textarea";
import ClaimTreeSelector from "../../ClaimTreeSelector";
import useAppState from "context/useAppState";
import MonitorIcon from "@mui/icons-material/Monitor";
import MonitorModal from "./MonitorModal";
import { getMonitoringSetting } from "services/monitoring";

interface ExaminerCitationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  patentData: any;
  projectId: string;
  citationsData?: any;
}

interface CitationItem {
  cited_patent: string;
  cited_id: string;
  date: string;
  citationType?: string;
  is_new?: boolean;
}

const ExaminerCitationsModal: React.FC<ExaminerCitationsModalProps> = ({
  isOpen,
  onClose,
  patentData,
  projectId,
  citationsData: initialCitationsData,
}) => {
  const [isShowMonitor, setIsShowMonitor] = useState(false);
  const [monitoringActive, setMonitoringActive] = useState(false);

  const fetchMonitoringStatus = async () => {
    if (!projectId || !patentData?.id) return;
    try {
      const res: any = await getMonitoringSetting(projectId, patentData.id, "examiner_citations");
      setMonitoringActive(res?.is_enabled || false);
    } catch {
      setMonitoringActive(false);
    }
  };

  useEffect(() => {
    if (isOpen && projectId) fetchMonitoringStatus();
  }, [isOpen, projectId]);

  // Create a wrapper for onClose that resets state
  const handleClose = () => {
    resetState();
    onClose();
  };
  // Two-step modal state
  const [currentStep, setCurrentStep] = useState<"citations" | "vijilize">(
    "citations"
  );

  // Citations step state
  const [selectedCount, setSelectedCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCitations, setSelectedCitations] = useState<string[]>([]);
  const [activeListIndex, setActiveListIndex] = useState(0);
  const [activeListType, setActiveListType] = useState<string>(
    "non_final_rejection"
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isAddingToProject, setIsAddingToProject] = useState(false);
  const [isVijilizing, setIsVijilizing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [citationsData, setCitationsData] = useState<any>(null);
  const [totalCitations, setTotalCitations] = useState(0);
  const [citations, setCitations] = useState<any[]>([]);
  const [selectedPatentNumbers, setSelectedPatentNumbers] = useState<string[]>(
    []
  );
  const [documentTransactionHistory, setDocumentTransactionHistory] = useState<
    string | null
  >(null);

  // Vijilize step state
  const [vijilizeName, setVijilizeName] = useState<string>("");
  const [vijilizeDescription, setVijilizeDescription] = useState<string>("");
  const [activeIndex, setActiveIndex] = useState<number>(1); // Source accordion open by default (1)
  const [sourcePatents, setSourcePatents] = useState<string[]>([]);
  const [comparePatents, setComparePatents] = useState<string[]>([]);
  const [useOnlyIndependentClaims, setUseOnlyIndependentClaims] = useState<boolean>(false);
  const [selectSpecificClaims, setSelectSpecificClaims] = useState<boolean>(false);
  // patent_id -> { claimKeys, loading, structure }
  const [patentClaims, setPatentClaims] = useState<Record<string, { claimKeys: string[]; loading: boolean; structure?: string }>>({});
  // patent_id -> selected claim keys
  const [selectedClaims, setSelectedClaims] = useState<Record<string, string[]>>({});
  const [citationDataMap, setCitationDataMap] = useState<
    Record<string, CitationItem>
  >({});

  // Selected patent data for source and compare
  const [selectedPatentData, setSelectedPatentData] = useState<any[]>([]);

  // Define citation types
  const citationTypes = [
    {
      key: "non_final_rejection",
      title: "Non-Final Rejection",
    },
    {
      key: "final_rejection",
      title: "Final Rejection",
    },
    {
      key: "list_of_references_cited_by_examiner",
      title: "List of references cited by examiner",
    },
    {
      key: "references_cited_by_applicant",
      title: "References cited by applicant and considered by examiner",
    },
  ];

  // Fetch claims for each source patent when "Select claims" is enabled
  useEffect(() => {
    if (!selectSpecificClaims || !isOpen) return;
    sourcePatents.forEach((pid) => {
      if (patentClaims[pid]) return; // already fetched
      setPatentClaims((prev) => ({ ...prev, [pid]: { claimKeys: [], loading: true } }));
      getPatentDetails(pid)
        .then((res: any) => {
          const data = res?.data || res || {};
          const claims = data.claims || {};
          const structure = data.structure || "";
          const keys = Object.keys(claims).sort((a, b) => {
            const numA = parseInt(a.replace(/\D/g, "")) || 0;
            const numB = parseInt(b.replace(/\D/g, "")) || 0;
            return numA - numB;
          });
          setPatentClaims((prev) => ({ ...prev, [pid]: { claimKeys: keys, loading: false, structure } }));
          setSelectedClaims((prev) => ({ ...prev, [pid]: [] }));
        })
        .catch(() => {
          setPatentClaims((prev) => ({ ...prev, [pid]: { claimKeys: [], loading: false } }));
        });
    });
  }, [selectSpecificClaims, isOpen]);

  // Parse Mermaid structure to get child -> parent mapping
  const parseMermaidParentMap = (structure: string): Record<string, string> => {
    const parentMap: Record<string, string> = {};
    const nodeMap: Record<string, string> = {};
    const nodeRegex = /([A-Za-z][A-Za-z0-9]*)\s*\[\s*"?\s*Claim\s+(\d+)/gi;
    let match;
    while ((match = nodeRegex.exec(structure)) !== null) {
      nodeMap[match[1]] = `claim${match[2]}`;
    }
    const edgeRegex = /([A-Za-z][A-Za-z0-9]*)\s*-->\s*([A-Za-z][A-Za-z0-9]*)/g;
    while ((match = edgeRegex.exec(structure)) !== null) {
      const parentClaim = nodeMap[match[1]];
      const childClaim = nodeMap[match[2]];
      if (parentClaim && childClaim) {
        parentMap[childClaim] = parentClaim;
      }
    }
    return parentMap;
  };

  // Get all ancestors of a claim (walk up the tree)
  const getAncestors = (claimKey: string, parentMap: Record<string, string>): string[] => {
    const ancestors: string[] = [];
    let current = claimKey;
    while (parentMap[current]) {
      ancestors.push(parentMap[current]);
      current = parentMap[current];
    }
    return ancestors;
  };

  const toggleClaimSelection = (patentId: string, claimKey: string) => {
    setSelectedClaims((prev) => {
      const current = prev[patentId] || [];
      if (current.includes(claimKey)) {
        return { ...prev, [patentId]: current.filter((k) => k !== claimKey) };
      } else {
        // Auto-select all ancestors when selecting a dependent claim
        const structure = patentClaims[patentId]?.structure;
        let toSelect = [claimKey];
        if (structure) {
          const parentMap = parseMermaidParentMap(structure);
          toSelect = [...toSelect, ...getAncestors(claimKey, parentMap)];
        }
        const newSelection = [...new Set([...current, ...toSelect])];
        return { ...prev, [patentId]: newSelection };
      }
    });
  };

  const toggleAllClaims = (patentId: string) => {
    const allKeys = patentClaims[patentId]?.claimKeys || [];
    const current = selectedClaims[patentId] || [];
    if (current.length === allKeys.length) {
      setSelectedClaims((prev) => ({ ...prev, [patentId]: [] }));
    } else {
      setSelectedClaims((prev) => ({ ...prev, [patentId]: [...allKeys] }));
    }
  };

  // Function to reset all state variables to their default values
  const resetState = () => {
    // Reset two-step modal state
    setCurrentStep("citations");

    // Reset citations step state
    setSelectedCount(0);
    setSearchQuery("");
    setSelectedCitations([]);
    setActiveListIndex(0);
    setActiveListType("non_final_rejection");
    setIsLoading(false);
    setIsAddingToProject(false);
    setIsVijilizing(false);
    setError(null);
    setCitationsData(null);
    setTotalCitations(0);
    setCitations([]);
    setSelectedPatentNumbers([]);
    setDocumentTransactionHistory(null);

    // Reset vijilize step state
    setVijilizeName("");
    setVijilizeDescription("");
    setActiveIndex(1); // Source accordion open by default
    setSourcePatents([]);
    setComparePatents([]);
    setUseOnlyIndependentClaims(false);
    setSelectSpecificClaims(false);
    setPatentClaims({});
    setSelectedClaims({});
    setCitationDataMap({});

    // Reset selected patent data
    setSelectedPatentData([]);
  };

  useEffect(() => {
    if (isOpen && patentData?.id && patentData?.patent_number) {
      setCitations([]);
      setCitationsData([]);
      fetchExaminerCitations();
      setSelectedCitations([]);
      setSelectedCount(0);
      setSearchQuery("");
    }
  }, [isOpen, patentData?.id, patentData?.patent_number]);

  // Effect to reset state when modal is closed
  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen]);

  useEffect(() => {
    setCitations([]);
    if (isOpen && citationsData) {
      const newCitations = citationsData[activeListType] || [];
      setCitations(newCitations);
    }
    return () => {
      setCitations([]);
    };
  }, [isOpen, activeListType, citationsData]);

  // Update activeListType when activeListIndex changes
  useEffect(() => {
    setActiveListType(citationTypes[activeListIndex].key);
  }, [activeListIndex]);

  const isValidPatentNumber = (citation: string): boolean => {
    return /^US\d+/.test(citation);
  };

  const fetchExaminerCitations = async () => {
    setIsLoading(true);
    setError(null);
    setSelectedCitations([]);
    setSelectedCount(0);

    try {
      // Use initialCitationsData if provided, otherwise fetch from API
      if (initialCitationsData) {
        setCitationsData(initialCitationsData);
        processExaminerCitationsData(initialCitationsData);
      } else {
        let selectedTeam: any = JSON.parse(
          localStorage.getItem("selectedTeam") as string
        );
        let team_id: string = selectedTeam.currentTeamId;
        const response = await getExaminerCitationsDetails(
          patentData.patent_number,
          patentData.s3_file_path,
          projectId,
          team_id
        );
        setCitationsData(response.data);
        processExaminerCitationsData(response.data);
      }
    } catch (error) {
      console.error("Error processing examiner citations:", error);
      setError("Failed to load examiner citations. Please try again later.");
      Toast.error("Error", "Failed to load examiner citations");
    } finally {
      setIsLoading(false);
    }
  };

  const processExaminerCitationsData = (data: any) => {
    // Calculate total citations with proper typing
    const total = Object.values(data || {}).reduce<number>((acc, citations) => {
      if (Array.isArray(citations)) {
        return acc + citations.length;
      }
      return acc;
    }, 0);
    setTotalCitations(total);

    // Find first non-empty list
    const firstNonEmptyIndex = citationTypes.findIndex(
      (type) => (data?.[type.key]?.length || 0) > 0
    );
    if (firstNonEmptyIndex !== -1) {
      setActiveListIndex(firstNonEmptyIndex);
    }

    // Check if document_transaction_history exists in the response
    if (data?.document_transaction_history) {
      setDocumentTransactionHistory(data.document_transaction_history);
    } else {
      setDocumentTransactionHistory(null);
    }
  };

  const handleSelectAll = () => {
    // Get all valid citations that match the current search filter
    const validCitations: string[] = [];

    // Check if there's a search query
    if (searchQuery.trim() !== "") {
      // Apply the search filter across ALL citation types, not just the active one
      citationTypes.forEach((type) => {
        const listData = citationsData?.[type.key] || [];

        // Filter citations based on search query
        const filteredCitations = listData.filter((citation: CitationItem) => {
          // Normalize both the citation patent number and search query
          const normalizedCitation = citation.cited_patent
            .toLowerCase()
            .replace(/[,\/]/g, "");
          const normalizedQuery = searchQuery
            .toLowerCase()
            .replace(/[,\/]/g, "");

          return (
            normalizedCitation.includes(normalizedQuery) ||
            citation.cited_patent
              .toLowerCase()
              .includes(searchQuery.toLowerCase())
          );
        });

        // Only include valid patent numbers from the filtered results
        filteredCitations.forEach((citation: CitationItem) => {
          if (isValidPatentNumber(citation.cited_patent)) {
            validCitations.push(`citation-${type.key}-${citation.cited_id}`);
          }
        });
      });
    } else {
      // If no search query, use the original logic for all citation types
      citationTypes.forEach((type) => {
        const listData = citationsData?.[type.key] || [];
        listData.forEach((citation: CitationItem) => {
          // Only include valid patent numbers
          if (isValidPatentNumber(citation.cited_patent)) {
            validCitations.push(`citation-${type.key}-${citation.cited_id}`);
          }
        });
      });
    }

    // Calculate total valid citations count
    const totalValidCitationsCount = validCitations.length;

    // If all valid citations are already selected, deselect all
    if (selectedCount === totalValidCitationsCount) {
      setSelectedCitations([]);
      setSelectedCount(0);
    } else {
      // Otherwise, select all valid citations
      setSelectedCitations(validCitations);
      setSelectedCount(totalValidCitationsCount);
    }
  };

  const getSelectedCountForCurrentList = () => {
    return citations.filter((citation: CitationItem) =>
      selectedCitations.includes(
        `citation-${activeListType}-${citation.cited_id}`
      )
    ).length;
  };

  // Function to get selected count for a specific list type
  const getSelectedCountForListType = (listType: string) => {
    const listData = citationsData?.[listType] || [];
    return listData.filter((citation: CitationItem) =>
      selectedCitations.includes(`citation-${listType}-${citation.cited_id}`)
    ).length;
  };

  const handleCitationSelect = (citationId: string, isChecked: boolean) => {
    // Extract the citation from the citations array
    const citation = citations.find(
      (c: any) => `citation-${activeListType}-${c.cited_id}` === citationId
    );

    // Only proceed if it's a valid patent number
    if (citation && isValidPatentNumber(citation.cited_patent)) {
      if (isChecked) {
        setSelectedCitations([...selectedCitations, citationId]);
        setSelectedCount(selectedCount + 1);
      } else {
        setSelectedCitations(
          selectedCitations.filter((id) => id !== citationId)
        );
        setSelectedCount(selectedCount - 1);
      }

      // Update the selected patent numbers list
      const patentNumber = citation.cited_patent;
      if (isChecked) {
        setSelectedPatentNumbers([...selectedPatentNumbers, patentNumber]);
      } else {
        setSelectedPatentNumbers(
          selectedPatentNumbers.filter((num) => num !== patentNumber)
        );
      }
    }
  };

  const handleClear = () => {
    setSelectedCitations([]);
    setSelectedCount(0);
  };

  const handleAddToProject = async () => {
    // Prevent multiple API calls if already in progress
    if (isAddingToProject) return;

    try {
      // Set loading state
      setIsAddingToProject(true);

      // Collect all selected citations from all list types using the same logic as handleVijilize
      let allSelectedCitations: CitationItem[] = [];
      const seenNormalizedPatents = new Set<string>();
      const uniqueSelectedCitations: CitationItem[] = [];
      const patentSourcesMap: Record<string, string> = {};

      // Go through each citation type and collect selected items, filtering duplicates by normalized patent number
      citationTypes.forEach((type) => {
        const citationsOfType = citationsData?.[type.key] || [];
        const selectedCitationsOfType = citationsOfType.filter(
          (citation: CitationItem) =>
            selectedCitations.includes(
              `citation-${type.key}-${citation.cited_id}`
            )
        );
        selectedCitationsOfType.forEach((citation: CitationItem) => {
          const normalizedPatent = citation.cited_patent.replace(/[,\/]/g, "");
          if (!seenNormalizedPatents.has(normalizedPatent)) {
            seenNormalizedPatents.add(normalizedPatent);
            uniqueSelectedCitations.push({
              ...citation,
              citationType: type.title,
            });
          }
        });
      });

      // Extract patent numbers and filter for valid ones only
      const allSelectedPatents = uniqueSelectedCitations
        .filter((citation) =>
          isValidPatentNumber(citation.cited_patent.replace(/[,\/]/g, ""))
        )
        .map((citation) => citation.cited_patent.replace(/[,\/]/g, ""));

      // If no valid patents were selected, show an error and return
      if (allSelectedPatents.length === 0) {
        Toast.error("Error", "No valid patent numbers were selected");
        return;
      }

      // Prepare patent sources map using the same format as in handleSubmitVijilize
      uniqueSelectedCitations.forEach((citation) => {
        const patentNumber = citation.cited_patent.replace(/[,\/]/g, "");

        // Skip if not a valid patent number
        if (!isValidPatentNumber(patentNumber)) return;

        let dateString;
        if (citation.date) {
          try {
            const dateObj = new Date(citation.date);
            if (!isNaN(dateObj.getTime())) {
              dateString = dateObj.toLocaleDateString("en-US", {
                day: "numeric",
                month: "long",
                year: "numeric",
              });
            } else {
              dateString = citation.date;
            }
          } catch (e) {
            dateString = citation.date;
          }
        } else {
          dateString = new Date().toLocaleDateString("en-US", {
            day: "numeric",
            month: "long",
            year: "numeric",
          });
        }

        const sourceField = `${patentData.patent_number} - ${citation.citationType} - ${dateString}`;
        patentSourcesMap[patentNumber] = sourceField;
      });

      const selectedTeam = localStorage.getItem("selectedTeam")
        ? JSON.parse(localStorage.getItem("selectedTeam") as string)
        : null;
      const teamId = selectedTeam?.currentTeamId;

      // Create the payload for the API
      const addToProjectPayload = {
        patent_numbers: allSelectedPatents,
        team_id: teamId,
        project_id: projectId,
        patent_category: PROJECT_CONSTANTS.COMPARE,
        patent_type: PROJECT_CONSTANTS.PATENT,
        examiner_patent_sources: patentSourcesMap,
      };

      // Call the API to add citations to the project
      await addExaminerCitationsToProject(addToProjectPayload);

      // Show success message
      Toast.success(
        "Success",
        `Adding ${allSelectedPatents.length} patent${allSelectedPatents.length !== 1 ? "s" : ""
        } to project started. Check the Patents tab for progress.`
      );
    } catch (error: any) {
      console.error("Error adding patents to project:", error);

      Toast.error(
        "Error",
        "Failed to add citations to project. Please try again."
      );
    } finally {
      // Reset loading state
      setIsAddingToProject(false);
    }
  };

  const handleVijilize = () => {
    // Prevent multiple clicks
    if (isLoading) return;

    // Set loading temporarily to prevent multiple clicks
    setIsLoading(true);

    // Collect all selected citations from all list types
    let allSelectedCitations: CitationItem[] = [];
    const seenNormalizedPatents = new Set<string>();
    const uniqueSelectedCitations: CitationItem[] = [];

    // Go through each citation type and collect selected items, filtering duplicates by normalized patent number
    citationTypes.forEach((type) => {
      const citationsOfType = citationsData?.[type.key] || [];
      const selectedCitationsOfType = citationsOfType.filter(
        (citation: CitationItem) =>
          selectedCitations.includes(
            `citation-${type.key}-${citation.cited_id}`
          )
      );
      selectedCitationsOfType.forEach((citation: CitationItem) => {
        const normalizedPatent = citation.cited_patent.replace(/[,\/]/g, "");
        if (!seenNormalizedPatents.has(normalizedPatent)) {
          seenNormalizedPatents.add(normalizedPatent);
          uniqueSelectedCitations.push({
            ...citation,
            citationType: type.title,
          });
        }
      });
    });

    if (uniqueSelectedCitations.length === 0) {
      Toast.warning("Warning", "No references selected");
      return;
    }

    // Extract and normalize patent numbers by removing commas and slashes
    const patentNumbers = uniqueSelectedCitations.map((citation) => {
      return citation.cited_patent.replace(/[,\/]/g, "");
    });

    // Store the selected patent numbers for the next step
    setSelectedPatentNumbers(patentNumbers);

    const citationDataMap = uniqueSelectedCitations.reduce((map, citation) => {
      const normalizedPatent = citation.cited_patent.replace(/[,\/]/g, "");
      map[normalizedPatent] = citation;
      return map;
    }, {} as Record<string, CitationItem>);

    setCitationDataMap(citationDataMap);

    setVijilizeName("");
    setVijilizeDescription("");
    const selectedPatentDataArray = [
      // Add the current patent as a source patent
      {
        patent_id: patentData.id,
        file_name: patentData.patent_number,
      },
      ...uniqueSelectedCitations.map((citation) => ({
        patent_id: citation.cited_id,
        file_name: citation.cited_patent,
      })),
    ];

    setSelectedPatentData(selectedPatentDataArray);

    // Set the source patent to be the current patent
    setSourcePatents([patentData.id]);

    // Initialize all citation patents as compare patents (no duplicates)
    setComparePatents(
      uniqueSelectedCitations.map((citation) => citation.cited_id)
    );

    // Move to the second step
    setCurrentStep("vijilize");

    // Reset loading state
    setIsLoading(false);
  };

  // In the left sidebar click handler
  const handleListClick = (index: number, type: string) => {
    setActiveListIndex(index);
    setActiveListType(type);
  };

  // Handle name change in the vijilize form
  const handleNameChange = (e: any) => {
    setVijilizeName(e.target.value);
  };

  // Handle description change in the vijilize form
  const handleDescriptionChange = (e: any) => {
    setVijilizeDescription(e.target.value);

    // Adjust textarea height based on content
    let getTextArea = document.getElementById("vijilize-description");
    if (getTextArea) {
      if (e.target.value.length === 0) {
        e.target.style.height = "24px";
      } else {
        e.target.style.height = "auto";
        e.target.style.height = e.target.scrollHeight + "px";
      }
    }
  };

  // Toggle accordion sections in the vijilize form
  const toggleAccordion = (index: number) => {
    setActiveIndex(activeIndex === index ? 0 : index);
  };

  // Check if the submit button should be disabled
  const isSubmitDisabled = () => {
    if (!vijilizeName.trim()) return true;
    if (selectSpecificClaims) {
      const totalSelected = Object.values(selectedClaims).reduce((sum, keys) => sum + keys.length, 0);
      if (totalSelected === 0) return true;
    }
    return false;
  };

  // Check if a citation type contains any new citations
  const hasCitationTypeNewItems = (typeKey: string) => {
    const citations = citationsData?.[typeKey] || [];
    return citations.some((citation: CitationItem) => citation.is_new);
  };

  // Go back to the citations selection step
  const handleBack = () => {
    setCurrentStep("citations");
  };

  // Handle the final vijilize submission
  const handleSubmitVijilize = async () => {
    if (isSubmitDisabled() || isVijilizing) {
      return;
    }

    // Set loading state
    setIsVijilizing(true);

    try {
      // Show loading toast
      // Toast.info("Processing", "Adding patents to your project...");

      // Get team ID from localStorage
      const selectedTeam = localStorage.getItem("selectedTeam")
        ? JSON.parse(localStorage.getItem("selectedTeam") as string)
        : null;
      const teamId = selectedTeam?.currentTeamId;

      // Get the active citation type title
      const activeCitationType =
        citationTypes.find((type) => type.key === activeListType)?.title ||
        "Unknown";

      // Create source patent data directly from patentData with file_id
      const sourcePatentData = {
        patent_id: patentData.id,
        file_id: patentData.file_id, // Use file_id from patentData
        file_name: patentData.patent_number,
        s3_file_path: patentData.s3_file_path,
        key: patentData.key,
      };

      const patentSourcesMap: Record<string, string> = {};

      selectedPatentNumbers.forEach((patentNumber) => {
        const citation = citationDataMap[patentNumber];
        if (citation) {
          let dateString;

          if (citation.date) {
            try {
              const dateObj = new Date(citation.date);

              if (!isNaN(dateObj.getTime())) {
                dateString = dateObj.toLocaleDateString("en-US", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                });
              } else {
                dateString = citation.date;
              }
            } catch (e) {
              dateString = citation.date;
            }
          } else {
            dateString = new Date().toLocaleDateString("en-US", {
              day: "numeric",
              month: "long",
              year: "numeric",
            });
          }

          const citationType = citation.citationType || activeCitationType;

          const sourceField = `${patentData.patent_number} - ${citationType} - ${dateString}`;
          patentSourcesMap[patentNumber] = sourceField;
        }
      });

      const vijilizePayload = {
        type: VIJILIZE_CONSTANTS.EXAMINER_CITATIONS,
        data: {
          patent_numbers: selectedPatentNumbers,
          team_id: teamId,
          project_id: projectId,
          name: vijilizeName,
          description: vijilizeDescription,
          source: [sourcePatentData],
          patent_category: PROJECT_CONSTANTS.COMPARE,
          patent_type: PROJECT_CONSTANTS.PATENT,
          examiner_patent_sources: patentSourcesMap,
          hide_patents: true,
          useOnlyIndependentClaims: useOnlyIndependentClaims,
          ...(selectSpecificClaims && { selectedClaims }),
        },
      };

      console.log(
        "Starting examiner citations vijilize with payload:",
        vijilizePayload
      );
      // Start the vijilization with the examiner_citations type
      const response = await startVijilize(vijilizePayload);

      // Show success toast
      Toast.success(
        "Success",
        `Examiner citations vijilization started successfully.`
      );

      // Close the modal
      handleClose();
    } catch (error: any) {
      console.error("Error processing examiner citations vijilize:", error);

      Toast.error("Error", "Failed to process vijilization. Please try again.");
    } finally {
      // Reset loading state
      setIsVijilizing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[999] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-black opacity-30"></div>

        <div className="relative bg-white rounded-lg w-[90vw] max-w-[1920px] max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="pl-6 pr-6 pt-6">
            <div className="flex items-center gap-x-2">
              <img
                src={allImgPaths.ExaminerCitationsModalIcon}
                alt=""
                className="w-10 h-10"
              />
              <div className="absolute right-6 top-6 flex items-center gap-x-2">
                <button
                  className={`walkthrough-monitor flex items-center gap-x-1.5 border rounded-lg px-3 py-1.5 text-sm font-interMedium transition-colors ${
                    monitoringActive
                      ? "border-[#1751D0] text-[#1751D0] bg-[#F5F8FD] hover:bg-[#EBF0FB]"
                      : "border-[#D0D5DD] text-[#7F7D83] hover:bg-[#F9FAFB]"
                  }`}
                  onClick={() => setIsShowMonitor(true)}
                >
                  <MonitorIcon sx={{ width: 18, height: 18, color: monitoringActive ? "#1751D0" : "#ADACB0" }} />
                  Monitor
                </button>
                <button
                  onClick={handleClose}
                  className="hover:bg-[#f2f4f7] rounded p-1"
                >
                  <img
                    src={allImgPaths.closeIcon}
                    alt="Close"
                    className="w-3 h-3"
                  />
                </button>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div>
              <h2 className="text-base font-interSemiBold text-[#0A090B]">
                View Examiner Citations - {patentData.patent_number}
              </h2>
              <p className="text-sm font-interRegular text-[#4F4D55]">
                Reference and compare with citations form the examiner
              </p>
            </div>
          </div>

          {currentStep === "citations" ? (
            <>
              {/* Citations Step Content */}
              {/* Selection Header */}
              <div className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-interSemiBold text-[#000000]">
                    Select Citations to vijilize or add to your project
                  </h3>
                  <div className="flex items-center gap-x-4">
                    <span className="text-xs font-interRegular text-[#4F4D55]">
                      {selectedCount} of {totalCitations} Selected
                    </span>
                    <button
                      onClick={handleSelectAll}
                      className="text-xs text-[#3062D4] hover:text-blue-800 font-interRegular"
                    >
                      Select All
                    </button>
                    <button
                      onClick={handleClear}
                      className="text-xs text-[#3062D4] hover:text-blue-800 font-interRegular"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex px-6 flex-1 min-h-0">
                {/* Left Sidebar - Citation Lists */}
                <div className="min-w-[35%] bg-[#F9FAFB] overflow-y-auto rounded-lg mr-6">
                  <div className="p-4 walkthrough-citation-lists">
                    <h3 className="text-base font-interMedium mb-4">
                      Citation Lists
                    </h3>
                    <div className="space-y-4">
                      {citationTypes.map((type, index) => {
                        const citations = citationsData?.[type.key] || [];
                        if (citations.length === 0) return null;

                        const latestDate =
                          citations.length > 0
                            ? new Date(
                              Math.max(
                                ...citations.map((item: CitationItem) =>
                                  new Date(item.date).getTime()
                                )
                              )
                            )
                            : null;

                        // Format date to show day and month year
                        const formattedDate = latestDate
                          ? `${latestDate.getDate()} ${latestDate.toLocaleString(
                            "default",
                            { month: "long" }
                          )} ${latestDate.getFullYear()}`
                          : "";

                        return (
                          <div
                            key={type.key}
                            className={`cursor-pointer relative ${type.key === activeListType ? "" : ""
                              }`}
                            onClick={() => handleListClick(index, type.key)}
                          >
                            {type.key === activeListType && (
                              <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#3062D4]"></div>
                            )}
                            <div className="flex items-start justify-between px-2 py-2 hover:bg-white">
                              <div className="flex items-start gap-x-3">
                                <img
                                  src={allImgPaths.folderIcon}
                                  alt="Folder"
                                  className="w-5 h-5 mt-0.5"
                                />
                                <div>
                                  <div className="flex items-center gap-2">
                                    <div className="text-sm font-interMedium text-[#101828]">
                                      {type.title}
                                    </div>
                                    {hasCitationTypeNewItems(type.key) && (
                                      <span
                                        className="w-2.5 h-2.5 rounded-full bg-[#E12121] inline-block"
                                        title="New citations found by monitoring"
                                      />
                                    )}
                                  </div>
                                  <div className="text-xs text-[#7F7D83] mt-1">
                                    {formattedDate}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-x-2 whitespace-nowrap">
                                <span className="text-xs text-[#7F7D83] whitespace-nowrap">
                                  {`${getSelectedCountForListType(
                                    type.key
                                  )} of ${citations.length}`}
                                </span>
                                <div className="text-gray-400 flex-shrink-0">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-5 w-5"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                {/* Main Content - Patent Lists */}
                <div className="flex-1">
                  <div>
                    {/* Search Bar and USPTO Link */}
                    <div className="mb-4 flex items-center justify-start gap-x-4">
                      {documentTransactionHistory ? (
                        <>
                          <a
                            href={documentTransactionHistory}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center text-blue-600 hover:text-blue-800 hover:underline font-interMedium text-sm"
                          >
                            USPTO Patent Center
                            <img
                              src={allImgPaths.externalLinkIcon}
                              alt="External Link"
                              className="ml-1 w-3 h-3 text-blue-600"
                            />
                          </a>
                          <div className="w-[70%]">
                            <SearchBar
                              img_url={allImgPaths.searchIcon}
                              placeholder="Search"
                              width="w-full"
                              value={searchQuery}
                              onChange={(value: string) =>
                                setSearchQuery(value)
                              }
                            />
                          </div>
                        </>
                      ) : (
                        <SearchBar
                          img_url={allImgPaths.searchIcon}
                          placeholder="Search"
                          width="w-full"
                          value={searchQuery}
                          onChange={(value: string) => setSearchQuery(value)}
                        />
                      )}
                    </div>

                    {/* Calculate filtered citations once and reuse */}
                    {(function () {
                      // Filter citations based on search query - define this once and reuse
                      const filteredCitations = (
                        citationsData?.[activeListType] || []
                      ).filter((citation: CitationItem) => {
                        // Skip filtering if search query is empty
                        if (searchQuery.trim() === "") return true;

                        // Normalize both the citation patent number and search query by removing commas and slashes
                        const normalizedCitation = citation.cited_patent
                          .toLowerCase()
                          .replace(/[,\/]/g, "");
                        const normalizedQuery = searchQuery
                          .toLowerCase()
                          .replace(/[,\/]/g, "");

                        // Check if the normalized citation includes the normalized query
                        return (
                          normalizedCitation.includes(normalizedQuery) ||
                          citation.cited_patent
                            .toLowerCase()
                            .includes(searchQuery.toLowerCase())
                        );
                      });

                      // If loading, show spinner
                      if (isLoading) {
                        return (
                          <div className="flex justify-center items-center h-40">
                            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#1751D0]"></div>
                          </div>
                        );
                      }

                      // If error, show error message
                      if (error) {
                        return (
                          <div className="text-center py-8">
                            <p className="text-red-500">{error}</p>
                            <button
                              onClick={fetchExaminerCitations}
                              className="mt-4 px-4 py-2 bg-[#1751D0] text-white rounded-md hover:bg-[#113B98]"
                            >
                              Retry
                            </button>
                          </div>
                        );
                      }

                      // If no citations at all
                      if (citations.length === 0) {
                        return (
                          <div className="text-center py-8">
                            <p className="text-gray-500">
                              No citations found for this patent.
                            </p>
                          </div>
                        );
                      }

                      // If filtered citations are empty but search query exists
                      if (
                        filteredCitations.length === 0 &&
                        searchQuery.trim() !== ""
                      ) {
                        return (
                          <div className="flex items-center justify-center h-[calc(100vh-520px)] w-[615px]">
                            <p className="text-gray-500 text-lg font-interMedium">
                              No results found for "{searchQuery}"
                            </p>
                          </div>
                        );
                      }

                      // Otherwise, show the references count and list
                      return (
                        <>
                          {/* References count and description */}
                          <div className="mb-4">
                            <h3 className="text-base font-interSemiBold text-[#272E35]">
                              {filteredCitations.length > 1
                                ? `${filteredCitations.length} References Found`
                                : `${filteredCitations.length} Reference Found`}
                            </h3>
                            <p className="text-sm text-[#272E35] font-interRegular">
                              Selected patents will be saved in the project's
                              Patents tab for future vijilizations.
                            </p>
                          </div>

                          {/* Patents List */}
                          <div className="space-y-3 overflow-y-auto overflow-x-hidden h-[calc(100vh-520px)]">
                            {filteredCitations.map((citation: CitationItem) => {
                              // Use cited_id for unique identification and cited_patent for display
                              const citationId = `citation-${activeListType}-${citation.cited_id}`;

                              return (
                                <div
                                  key={citationId}
                                  className={`walkthrough-citation-reference flex items-start gap-x-3 p-3 rounded-lg border-b border-[#F1F1F1] ${isValidPatentNumber(citation.cited_patent)
                                    ? "hover:bg-[#F5F8FD]"
                                    : ""
                                    }`}
                                >
                                  <div className="flex-shrink-0">
                                    <CustomCheckBox
                                      id={citationId}
                                      name={citationId}
                                      label=""
                                      checked={selectedCitations.includes(
                                        citationId
                                      )}
                                      onChange={(
                                        name: string,
                                        checked: boolean
                                      ) => {
                                        if (
                                          isValidPatentNumber(
                                            citation.cited_patent
                                          )
                                        ) {
                                          handleCitationSelect(
                                            citationId,
                                            checked
                                          );
                                        }
                                      }}
                                      customStyle={`flex-shrink-0 ${!isValidPatentNumber(
                                        citation.cited_patent
                                      )
                                        ? "hover-none"
                                        : ""
                                        }`}
                                      disabled={
                                        !isValidPatentNumber(
                                          citation.cited_patent
                                        )
                                      }
                                    />
                                  </div>
                                  <div
                                    className={`flex-1 ${isValidPatentNumber(citation.cited_patent)
                                      ? "cursor-pointer"
                                      : "cursor-not-allowed"
                                      }`}
                                    onClick={() => {
                                      if (
                                        isValidPatentNumber(
                                          citation.cited_patent
                                        )
                                      ) {
                                        handleCitationSelect(
                                          citationId,
                                          !selectedCitations.includes(
                                            citationId
                                          )
                                        );
                                      }
                                    }}
                                  >
                                    <div className="w-[555px]">
                                      <div className="flex items-center gap-2">
                                        <div className="relative inline-block overflow-visible">
                                          {citation.is_new && (
                                            <span
                                              className="absolute -top-1.5 -right-1 w-2.5 h-2.5 rounded-full bg-[#E12121] inline-block z-10 cursor-pointer"
                                              title="New citation found by monitoring"
                                            />
                                          )}
                                          <h4
                                            title={citation.cited_patent}
                                            className={`text-sm font-interSemiBold  ${!isValidPatentNumber(
                                              citation.cited_patent
                                            )
                                              ? `text-gray-500 truncate max-w-[calc(100%_-_225px)]`
                                              : "text-[#0F0F00E3]"
                                              }`}
                                          >
                                            {citation.cited_patent}
                                          </h4>
                                        </div>
                                        {!isValidPatentNumber(
                                          citation.cited_patent
                                        ) && (
                                            <span className="px-2 py-0.5 text-xs font-interMedium bg-gray-100 text-gray-500 rounded-full">
                                              Non-Patent-Literature-Unavailable
                                            </span>
                                          )}
                                      </div>
                                      <p className="text-sm text-[#7F7D83] font-interSemiBold mt-1">
                                        {new Date(citation.date).getDate()}{" "}
                                        {new Date(citation.date).toLocaleString(
                                          "default",
                                          { month: "long" }
                                        )}{" "}
                                        {new Date(citation.date).getFullYear()}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Vijilize Step Content */}
              <div className="px-6 flex-1 min-h-0 overflow-y-auto">
                <div className="">
                  {/* Notification */}
                  <div className="border border-[#ECECED] rounded-lg px-3 py-3 flex items-start gap-x-2 mb-3">
                    <img src={allImgPaths.InfoIconBlue} alt="" />
                    <p className="text-sm font-interMedium text-[#0A090B] leading-[1.6]">
                      Each vijilization takes some time to complete. You can
                      find your past and active ones in the Vijilizations tab.
                    </p>
                  </div>

                  {/* Content */}
                  {/* Name & Description row on top when claims tree is shown */}
                  {selectSpecificClaims && (
                    <div className="flex gap-x-4 mb-4">
                      <div className="flex-1">
                        <span className="text-[#2D2B32] font-interMedium text-sm inline-block pb-2">
                          Name
                        </span>
                        <InputTextField
                          label={""}
                          name={"vijilizeName"}
                          placeholder={"Enter name"}
                          onChange={handleNameChange}
                          value={vijilizeName}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="pb-2 flex items-center justify-between">
                          <span className="text-[#2D2B32] font-interMedium text-sm inline-block">
                            Description
                          </span>
                          <span className="text-xs text-[#7F7D83]">Optional</span>
                        </div>
                        <Textarea
                          name="vijilize-description"
                          placeholder="Enter description"
                          value={vijilizeDescription}
                          onChange={handleDescriptionChange}
                          id="vijilize-description"
                        />
                      </div>
                    </div>
                  )}

                  <div className={`walkthrough-vijilize-content ${selectSpecificClaims ? "" : "flex gap-x-8"}`}>
                    <div className={`walkthrough-claim-options px-3 py-3 rounded-lg border border-[#E6E6E6] ${selectSpecificClaims ? "w-full" : "w-[434px]"}`}>
                      <h4 className="text-sm font-interMedium text-[#0A090B] pb-3">
                        Data Used
                      </h4>

                      {/* Use only Independent Claims checkbox */}
                      <div className="mb-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={useOnlyIndependentClaims}
                            onChange={(e) => {
                              setUseOnlyIndependentClaims(e.target.checked);
                              if (e.target.checked) {
                                setSelectSpecificClaims(false);
                                setSelectedClaims({});
                              }
                            }}
                            className="w-4 h-4 text-[#1751D0] border border-gray-300 rounded focus:ring-[#1751D0] focus:ring-2"
                            disabled={selectSpecificClaims}
                          />
                          <span className={`text-sm font-interMedium ${selectSpecificClaims ? "text-[#9E9DA2]" : "text-[#0A090B]"}`}>
                            Use only Independent Claims
                          </span>
                        </label>
                      </div>

                      {/* Select specific claims checkbox */}
                      <div className="mb-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectSpecificClaims}
                            onChange={(e) => {
                              setSelectSpecificClaims(e.target.checked);
                              if (e.target.checked) {
                                setUseOnlyIndependentClaims(false);
                                setActiveIndex(1); // auto-expand source accordion
                              } else {
                                setSelectedClaims({});
                                setPatentClaims({});
                              }
                            }}
                            className="w-4 h-4 text-[#1751D0] border border-gray-300 rounded focus:ring-[#1751D0] focus:ring-2"
                            disabled={useOnlyIndependentClaims}
                          />
                          <span className={`text-sm font-interMedium ${useOnlyIndependentClaims ? "text-[#9E9DA2]" : "text-[#0A090B]"}`}>
                            Select claims
                          </span>
                        </label>
                      </div>

                      {/* Accordion */}
                      <div className={`overflow-y-auto pr-2 ${selectSpecificClaims ? "max-h-[400px]" : "max-h-60"}`}>
                        {/* Source Accordion */}
                        <div
                          onClick={() => toggleAccordion(1)}
                          className={`mb-3 last:mb-0 ${activeIndex === 1 ? "" : "accordion-hide"
                            }`}
                        >
                          {/* Header Section */}
                          <div className="border border-[#F0F0F0] px-2 py-2 bg-[#FDFDFD] cursor-pointer accordion-heading flex items-center justify-between">
                            <div className="flex items-center gap-4 w-full justify-between">
                              <p className="text-[#555F6D] text-sm font-interMedium">
                                Source{" "}
                                <span className="text-[#555F6D] text-sm font-interMedium">
                                  ({sourcePatents.length})
                                </span>
                              </p>
                              <img
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleAccordion(1);
                                }}
                                className="accordion-arrow cursor-pointer hover:bg-[#f2f4f7] rounded w-5"
                                src={allImgPaths.dropdownArrow}
                                alt="Toggle Dropdown"
                              />
                            </div>
                          </div>

                          {/* Content Section */}
                          <div
                            className={`accordion-content border border-[#F0F0F0] border-t-0 ${activeIndex === 1 ? "block" : "hidden"
                              }`}
                          >
                            {sourcePatents.length === 0 ? (
                              <div className="py-3 ml-3">
                                <p className="text-sm text-gray-500">
                                  No patents in source section
                                </p>
                              </div>
                            ) : (
                              sourcePatents.map((patentId, index) => {
                                const patentData = selectedPatentData.find(
                                  (p) => p.patent_id === patentId
                                );
                                return (
                                  <div
                                    key={index}
                                    className="py-3 ml-3 pr-3"
                                  >
                                    <p
                                      title={patentData?.file_name || patentId}
                                      className="text-sm font-interSemiBold text-[#1751D0] truncate max-w-[calc(100%_-_15px)] w-fit cursor-pointer"
                                    >
                                      {patentData?.file_name || patentId}
                                    </p>

                                    {/* Claim tree/chips when Select claims is enabled */}
                                    {selectSpecificClaims && (
                                      <div className="mt-2">
                                        {patentClaims[patentId]?.loading ? (
                                          <span className="text-xs text-[#7F7D83]">Loading claims...</span>
                                        ) : patentClaims[patentId]?.claimKeys?.length > 0 ? (
                                          <>
                                            {/* All / Deselect All toggle */}
                                            <div className="mb-2">
                                              <button
                                                type="button"
                                                onClick={() => toggleAllClaims(patentId)}
                                                className={`px-2 py-0.5 rounded-full text-xs font-interMedium border transition-colors ${(selectedClaims[patentId]?.length || 0) === patentClaims[patentId]?.claimKeys.length
                                                  ? "bg-[#1751D0] text-white border-[#1751D0]"
                                                  : "bg-white text-[#4F4D55] border-[#D0D5DD] hover:border-[#1751D0]"
                                                  }`}
                                              >
                                                All
                                              </button>
                                            </div>

                                            {/* Render tree if structure is available, otherwise fall back to chips */}
                                            {patentClaims[patentId]?.structure ? (
                                              <ClaimTreeSelector
                                                structure={patentClaims[patentId].structure!}
                                                claimKeys={patentClaims[patentId].claimKeys}
                                                selectedClaims={selectedClaims[patentId] || []}
                                                onToggleClaim={(claimKey) => toggleClaimSelection(patentId, claimKey)}
                                              />
                                            ) : (
                                              <div className="flex flex-wrap gap-1.5">
                                                {patentClaims[patentId].claimKeys.map((claimKey: string) => {
                                                  const isSelected = selectedClaims[patentId]?.includes(claimKey);
                                                  const claimNum = claimKey.replace(/\D/g, "");
                                                  return (
                                                    <button
                                                      key={claimKey}
                                                      type="button"
                                                      onClick={() => toggleClaimSelection(patentId, claimKey)}
                                                      className={`px-2 py-0.5 rounded-full text-xs font-interMedium border transition-colors ${isSelected
                                                        ? "bg-[#1751D0] text-white border-[#1751D0]"
                                                        : "bg-white text-[#4F4D55] border-[#D0D5DD] hover:border-[#1751D0]"
                                                        }`}
                                                    >
                                                      Claim {claimNum}
                                                    </button>
                                                  );
                                                })}
                                              </div>
                                            )}
                                          </>
                                        ) : (
                                          <span className="text-xs text-[#7F7D83]">No claims found</span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>

                        {/* Compare Accordion */}
                        <div
                          onClick={() => toggleAccordion(2)}
                          className={`mb-3 last:mb-0 ${activeIndex === 2 ? "" : "accordion-hide"
                            }`}
                        >
                          {/* Header Section */}
                          <div className="border border-[#F0F0F0] px-2 py-2 bg-[#FDFDFD] cursor-pointer accordion-heading flex items-center justify-between">
                            <div className="flex items-center gap-4 w-full justify-between">
                              <p className="text-[#555F6D] text-sm font-interMedium">
                                Compare{" "}
                                <span className="text-[#555F6D] text-sm font-interMedium">
                                  ({comparePatents.length})
                                </span>
                              </p>
                              <img
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleAccordion(2);
                                }}
                                className="accordion-arrow cursor-pointer hover:bg-[#f2f4f7] rounded w-5"
                                src={allImgPaths.dropdownArrow}
                                alt="Toggle Dropdown"
                              />
                            </div>
                          </div>

                          {/* Content Section */}
                          <div
                            className={`accordion-content border border-[#F0F0F0] border-t-0 ${activeIndex === 2 ? "block" : "hidden"
                              }`}
                          >
                            {comparePatents.length === 0 ? (
                              <div className="py-3 ml-3">
                                <p className="text-sm text-gray-500">
                                  No patents in compare section
                                </p>
                              </div>
                            ) : (
                              comparePatents.map((patentId, index) => {
                                const patentData = selectedPatentData.find(
                                  (p) => p.patent_id === patentId
                                );
                                return (
                                  <div
                                    key={index}
                                    className="py-3 ml-3 flex justify-between items-center pr-3"
                                  >
                                    <p
                                      title={patentData?.file_name || patentId}
                                      className="text-sm font-interSemiBold text-[#1751D0] truncate max-w-[calc(100%_-_15px)] w-fit cursor-pointer"
                                    >
                                      {patentData?.file_name || patentId}
                                    </p>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {!selectSpecificClaims && (
                    <div className="w-1/2 flex-shrink-0">
                      <div className="mb-6">
                        <span className="text-[#2D2B32] font-interMedium text-sm inline-block pb-2">
                          Name
                        </span>
                        <InputTextField
                          label={""}
                          name={"vijilizeName"}
                          placeholder={"Enter name"}
                          onChange={handleNameChange}
                          value={vijilizeName}
                        />
                      </div>

                      <div className="">
                        <div className="pb-2 flex items-center justify-between">
                          <span className="text-[#2D2B32] font-interMedium text-sm inline-block">
                            Description
                          </span>
                          <span className="text-xs text-[#7F7D83]">
                            Optional
                          </span>
                        </div>
                        <Textarea
                          name="vijilize-description"
                          placeholder="Enter description"
                          value={vijilizeDescription}
                          onChange={handleDescriptionChange}
                          id="vijilize-description"
                        />
                      </div>
                    </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Footer */}
          <div className="shrink-0 p-6 bg-white border-t border-[#E6E6E6]">
            <div className="flex justify-between max-w-full gap-4">
              {currentStep === "citations" ? (
                /* Citations step buttons */
                <>
                  <button
                    onClick={handleClose}
                    className="flex-1 px-4 py-3 text-sm font-interMedium text-[#4F4D55] hover:bg-gray-50 rounded-md border border-[#E6E6E6]"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddToProject}
                    disabled={selectedCount === 0 || isAddingToProject}
                    className={`walkthrough-add-to-project flex-1 px-4 py-3 text-sm font-interMedium text-[#4F4D55] hover:bg-gray-50 border border-[#E6E6E6] rounded-md ${selectedCount === 0 || isAddingToProject
                      ? "button-disabled"
                      : ""
                      }`}
                  >
                    Add to Project
                  </button>
                  <button
                    onClick={handleVijilize}
                    disabled={selectedCount === 0 || isLoading}
                    className={`walkthrough-next-vijilize flex-1 px-4 py-3 text-sm font-interMedium text-white bg-[#3062D4] hover:bg-blue-700 rounded-md ${selectedCount === 0 || isLoading ? "button-disabled" : ""
                      }`}
                  >
                    Next
                  </button>
                </>
              ) : (
                /* Vijilize step buttons */
                <>
                  <button
                    onClick={handleBack}
                    className="flex-1 px-4 py-3 text-sm font-interMedium text-[#4F4D55] hover:bg-gray-50 rounded-md border border-[#E6E6E6]"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleSubmitVijilize}
                    disabled={isSubmitDisabled() || isVijilizing}
                    className={`walkthrough-vijilize-submit flex-1 px-4 py-3 text-sm font-interMedium text-white bg-[#3062D4] hover:bg-blue-700 rounded-md ${isSubmitDisabled() || isVijilizing
                      ? "button-disabled"
                      : ""
                      }`}
                  >
                    Vijilize
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <MonitorModal
        isShow={isShowMonitor}
        setIsShow={setIsShowMonitor}
        projectId={projectId}
        level="row"
        rowId={patentData?.id}
        itemType="patent"
        checkType="examiner_citations"
        onSaved={fetchMonitoringStatus}
      />
    </div>
  );
};

export default ExaminerCitationsModal;
